// src/pages/Home/NosEquipes.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getAllTeamMembers } from "../../services/teamServices";
import {
  FaEnvelope,
  FaPhone,
  FaInfoCircle,
  FaTimes,
  FaUserTie,
} from "react-icons/fa";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

const NosEquipes = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modale "infos"
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoMember, setInfoMember] = useState(null);

  const baseUrl = useMemo(
    () => (process.env.REACT_APP_API_URL || "").replace(/\/$/, ""),
    [],
  );

  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    if (
      /^https?:\/\//i.test(s) ||
      s.startsWith("data:") ||
      s.startsWith("blob:")
    )
      return s;
    if (s.startsWith("/uploads/")) return `${baseUrl}${s}`;
    return s; // /img/... ou autre
  };

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const data = await getAllTeamMembers();
        const arr = Array.isArray(data) ? data : [];

        // Tri: categorie puis position
        arr.sort((a, b) => {
          const ca = (a.categorie || "").toLowerCase();
          const cb = (b.categorie || "").toLowerCase();
          if (ca < cb) return -1;
          if (ca > cb) return 1;
          const pa = Number(a.position ?? 9999);
          const pb = Number(b.position ?? 9999);
          return pa - pb;
        });

        setTeam(arr);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des membres du comité :",
          error,
        );
        setTeam([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, []);

  const defaultAvatar = "/img/user_avatar.jpg";

  // Regroupement par catégorie (optionnel mais très lisible)
  const grouped = useMemo(() => {
    const map = new Map();
    for (const m of team) {
      const key = (m.categorie || "Autres").trim() || "Autres";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    }
    return Array.from(map.entries()); // [ [categorie, members[]], ... ]
  }, [team]);

  const openInfos = (member) => {
    setInfoMember(member);
    setInfoOpen(true);
  };
  const closeInfos = () => {
    setInfoOpen(false);
    setInfoMember(null);
  };

  // Loading skeleton
  if (loading) {
    return (
      <section
        className="py-10 md:py-14 bg-slate-100 dark:bg-slate-900"
        id="equipes"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center mb-8 animate-pulse">
            <div className="h-4 w-28 bg-slate-200 dark:bg-slate-700 rounded mb-3" />
            <div className="h-7 w-64 bg-slate-200 dark:bg-slate-700 rounded" />
            <div className="h-4 w-96 max-w-full bg-slate-200 dark:bg-slate-700 rounded mt-3" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl bg-white/70 dark:bg-slate-800/60 ring-1 ring-slate-200/70 dark:ring-slate-700/70 p-5 animate-pulse"
              >
                <div className="h-20 w-20 rounded-full bg-slate-200 dark:bg-slate-700 mx-auto" />
                <div className="h-4 w-2/3 bg-slate-200 dark:bg-slate-700 rounded mt-4 mx-auto" />
                <div className="h-3 w-1/2 bg-slate-200 dark:bg-slate-700 rounded mt-2 mx-auto" />
                <div className="h-8 w-28 bg-slate-200 dark:bg-slate-700 rounded mt-4 mx-auto" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!team.length) return null;

  return (
    <section
      className="py-10 md:py-14 bg-slate-100 dark:bg-slate-900 dark:text-white"
      id="equipes"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Titre */}
        <div className="flex flex-col items-center text-center mb-7 md:mb-10">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-primary mb-2">
            Le comité
          </p>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
            Les membres du comité
          </h2>
          <p className="mt-2 text-xs md:text-sm text-slate-600 dark:text-slate-400 max-w-xl">
            Bureau, élus et responsables de commissions qui font vivre le Judo
            girondin.
          </p>
        </div>

        {/* Groupes */}
        <div className="space-y-10">
          {grouped.map(([cat, members]) => (
            <div key={cat}>
              <div className="flex items-center justify-between gap-3 mb-4">
                <h3 className="text-sm md:text-base font-bold uppercase tracking-widest text-slate-700 dark:text-slate-200">
                  {cat}
                </h3>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {members.length} membre{members.length > 1 ? "s" : ""}
                </span>
              </div>

              {/* Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {members.map((member) => {
                  const fullName =
                    `${member.prenom || ""} ${member.nom || ""}`.trim();
                  const avatar = toAbsolute(member.photoURL) || defaultAvatar;

                  const hasMail = !!(member.mail_member || "").trim();
                  const hasTel = !!(member.tel_member || "").trim();
                  const hasBio = !!(member.bio_member || "").trim();

                  return (
                    <div
                      key={member.id}
                      className="group rounded-2xl bg-white/80 dark:bg-slate-800/60 ring-1 ring-slate-200/70 dark:ring-slate-700/70 p-5 shadow-sm hover:shadow-md transition"
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className="relative">
                          <img
                            src={avatar}
                            alt={fullName}
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.src = defaultAvatar;
                            }}
                            className="h-20 w-20 rounded-full object-cover shadow ring-2 ring-white dark:ring-slate-800"
                          />
                          <span className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 grid place-items-center ring-2 ring-white dark:ring-slate-800">
                            <FaUserTie className="text-[12px]" />
                          </span>
                        </div>

                        <h4 className="mt-4 text-base font-bold text-slate-900 dark:text-slate-50">
                          {fullName}
                        </h4>

                        {member.fonction ? (
                          <div
                            className="mt-1 text-xs md:text-sm text-slate-600 dark:text-slate-300 italic"
                            dangerouslySetInnerHTML={{
                              __html: member.fonction,
                            }}
                          />
                        ) : (
                          <p className="mt-1 text-xs md:text-sm text-slate-500 dark:text-slate-400">
                            —
                          </p>
                        )}

                        {/* Icônes infos (seulement si présent) */}
                        {(hasMail || hasTel || hasBio) && (
                          <div className="mt-4 flex items-center justify-center gap-3">
                            {hasMail && (
                              <a
                                href={`mailto:${member.mail_member}`}
                                className="h-10 w-10 rounded-xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-slate-50 dark:bg-slate-900/40 grid place-items-center text-slate-700 dark:text-slate-200 hover:scale-[1.03] transition"
                                title="Envoyer un email"
                              >
                                <FaEnvelope />
                              </a>
                            )}

                            {hasTel && (
                              <a
                                href={`tel:${String(member.tel_member).replace(/\s+/g, "")}`}
                                className="h-10 w-10 rounded-xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-slate-50 dark:bg-slate-900/40 grid place-items-center text-slate-700 dark:text-slate-200 hover:scale-[1.03] transition"
                                title="Appeler"
                              >
                                <FaPhone />
                              </a>
                            )}

                            {(hasBio || hasMail || hasTel) && (
                              <button
                                type="button"
                                onClick={() => openInfos(member)}
                                className="h-10 w-10 rounded-xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-slate-50 dark:bg-slate-900/40 grid place-items-center text-slate-700 dark:text-slate-200 hover:scale-[1.03] transition"
                                title="Voir les informations"
                              >
                                <FaInfoCircle />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modale Infos */}
      {infoOpen && infoMember && (
        <InfoModal member={infoMember} onClose={closeInfos} />
      )}
    </section>
  );
};

function InfoModal({ member, onClose }) {
  const fullName = `${member.prenom || ""} ${member.nom || ""}`.trim();

  const hasMail = !!(member.mail_member || "").trim();
  const hasTel = !!(member.tel_member || "").trim();
  const hasBio = !!(member.bio_member || "").trim();

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white dark:bg-slate-900">
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 flex items-center justify-between">
          <div className="text-white">
            <div className="text-lg font-extrabold">{fullName}</div>
            <div className="text-xs opacity-90">Informations</div>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white grid place-items-center"
            title="Fermer"
            type="button"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {member.fonction && (
            <div className="text-sm text-slate-700 dark:text-slate-200">
              <div className="font-semibold mb-1">Fonction</div>
              <div dangerouslySetInnerHTML={{ __html: member.fonction }} />
            </div>
          )}

          {(hasMail || hasTel) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {hasMail && (
                <a
                  className="rounded-xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-slate-50 dark:bg-slate-950 px-4 py-3 text-sm hover:bg-white dark:hover:bg-slate-900 transition"
                  href={`mailto:${member.mail_member}`}
                >
                  <div className="font-semibold">Email</div>
                  <div className="text-slate-600 dark:text-slate-300 break-all">
                    {member.mail_member}
                  </div>
                </a>
              )}
              {hasTel && (
                <a
                  className="rounded-xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-slate-50 dark:bg-slate-950 px-4 py-3 text-sm hover:bg-white dark:hover:bg-slate-900 transition"
                  href={`tel:${String(member.tel_member).replace(/\s+/g, "")}`}
                >
                  <div className="font-semibold">Téléphone</div>
                  <div className="text-slate-600 dark:text-slate-300">
                    {member.tel_member}
                  </div>
                </a>
              )}
            </div>
          )}

          {hasBio && (
            <div className="rounded-xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-slate-50 dark:bg-slate-950 p-4">
              <div className="font-semibold text-sm text-slate-800 dark:text-slate-100 mb-2">
                Biographie
              </div>
              <div
                className="prose prose-slate dark:prose-invert max-w-none text-sm"
                dangerouslySetInnerHTML={{ __html: member.bio_member }}
              />
            </div>
          )}

          {!hasBio && !hasMail && !hasTel && (
            <div className="text-sm text-slate-600 dark:text-slate-300">
              Aucune information complémentaire.
            </div>
          )}
        </div>

        <div className="px-5 pb-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-sm font-semibold bg-slate-900 text-white dark:bg-white dark:text-slate-900"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

export default NosEquipes;
